/**
 * The status a sample takes when it is moved into a location, from the customer's `QRCodes` / `Receive` configuration:
 *
 *     { "StatusByLocation": { "<location QR code or GUID>": "RECV" }, "OnlySetFrom": [ "", "LOGD", "PEND" ] }
 *
 * `OnlySetFrom` lists the statuses a sample may be in for the location's status to apply ("" is no status), so
 * scanning a tested sample at a reception desk can't send it back to received. Without it, only samples with no
 * status yet are changed.
 */

const DEFAULT_ONLY_SET_FROM = [ '' ];

/**
 * @typedef {{ StatusByLocation?: Record<string, string>, OnlySetFrom?: Array<string> }} ReceiveConfig
 */

/**
 * @param {any} pStatus
 * @return {string}
 */
function normalizeStatus(pStatus)
{
	return String(pStatus ?? '').trim().toUpperCase();
}

/**
 * @param {ReceiveConfig} pConfig
 * @param {{ QRCode?: string, GUIDLocation?: string }} pLocation
 * @return {string} the status configured for the location, or ''
 */
function getConfiguredStatus(pConfig, pLocation)
{
	const tmpMap = (pConfig && pConfig.StatusByLocation && typeof pConfig.StatusByLocation === 'object') ? pConfig.StatusByLocation : {};
	if (!pLocation)
	{
		return '';
	}
	return String((pLocation.QRCode && tmpMap[pLocation.QRCode]) || (pLocation.GUIDLocation && tmpMap[pLocation.GUIDLocation]) || '');
}

/**
 * What to do with a sample's status when it is moved into a location.
 *
 * @param {ReceiveConfig} pConfig
 * @param {{ QRCode?: string, GUIDLocation?: string }} pLocation
 * @param {string} pCurrentStatus
 * @return {{ Status: string, Kept?: string }} Status is the one to set ('' for no change); Kept is the current
 *   status when the location has one configured but the sample is past the point where it applies.
 */
function getStatusForMove(pConfig, pLocation, pCurrentStatus)
{
	const tmpStatus = getConfiguredStatus(pConfig, pLocation);
	const tmpCurrent = normalizeStatus(pCurrentStatus);
	if (!tmpStatus || normalizeStatus(tmpStatus) === tmpCurrent)
	{
		return { Status: '' };
	}
	const tmpAllowed = (Array.isArray(pConfig?.OnlySetFrom) ? pConfig.OnlySetFrom : DEFAULT_ONLY_SET_FROM).map(normalizeStatus);
	if (!tmpAllowed.includes(tmpCurrent))
	{
		return { Status: '', Kept: String(pCurrentStatus) };
	}
	return { Status: tmpStatus };
}

module.exports =
{
	DEFAULT_ONLY_SET_FROM,
	getConfiguredStatus,
	getStatusForMove,
};
